import { computed, ref, Ref, watch } from '@vue/composition-api';
import axios from 'axios';

interface XhrConfig {
	/**
	 * The URL that will be requested in the request
	 */
	url: string;

	/**
	 * The HTTP the resource is going to be requested with
	 */
	method: 'post' | 'get' | 'put' | 'delete' | 'patch';

	/**
	 * A method that returns an object containing the parameters the resource will be requested with
	 * @returns Record<string, any>
	 */
	params?: () => Record<string, any>;
}

interface FetchConfig {
	dependencies?: Ref | Ref[];

	paging?: {
		/**
		 * If enabled paginates the result set
		 */
		enabled: boolean;

		/**
		 * The server property that contains the maximum number of items
		 */
		maxItemsProp: string;
	};
}

/**
 *
 * @param list The ref that is going to be filled with result objects
 * @param resultsKey the key from the response that contains result objects
 * @param config config for xhr and behavior options
 * @returns
 */
export function useFetch<T>(list: Ref<T[]>, resultsKey: string, xhr: XhrConfig, config: FetchConfig) {
	const loading = ref(true);
	const error = ref(false);
	const totalItems = ref<number | null>(null);
	const currentPage = ref(0);

	const hasMoreItems = computed(() => {
		if (!totalItems.value) return false;

		return totalItems.value > list.value.length;
	});

	const hasError = computed(() => error.value === true);
	const isLoading = computed(() => loading.value === true);

	const resetItems = () => {
		currentPage.value = 0;
		error.value = false;
	};

	const fetchItems = async (reset = false) => {
		if (reset) resetItems();

		/**
		 * Prevent flickering when response time is too fast
		 */
		const timer = setTimeout(() => {
			loading.value = true;

			if (reset) list.value = [];
		}, 100);

		const params = xhr.params ? xhr.params() : {};

		if (config.paging?.enabled) {
			params['page'] = currentPage.value;
		}

		axios({
			method: xhr.method,
			url: xhr.url,
			params,
		})
			.then((response) => {
				clearTimeout(timer);

				if (config.paging?.enabled && !totalItems.value) {
					totalItems.value = response.data[config.paging.maxItemsProp];
				}

				list.value = reset ? response.data[resultsKey] : [...list.value, ...response.data[resultsKey]];
			})
			.catch(() => (error.value = true))
			.finally(() => (loading.value = false));
	};

	const loadMore = async () => {
		currentPage.value++;
		await fetchItems(false);
	};

	if (config.dependencies) {
		watch(
			config.dependencies,
			async () => {
				await fetchItems(true);
			},
			{ immediate: true },
		);
	}

	return {
		loadMore,
		hasMoreItems,
		hasError,
		isLoading,
		currentPage,
	};
}
